/**
 * Problems people have reported.
 *
 * READ-ONLY, deliberately. No triage state, no assignee, no resolve button:
 * the first version of a bug tracker that nobody uses is the one with a
 * workflow. This is a list you read, newest first.
 *
 * The description is the smallest thing on each row. What makes this screen
 * worth building is the envelope beside it — the report that prompted the
 * whole feature was a viewer whose player would not load, whose own theory
 * was wrong, and whose browser could have said so in one field.
 */
import { useState, type ReactNode } from 'react';
import type { BugReportShape } from '@tomo/contracts';
import { Shell } from '../components/Shell.jsx';
import { Empty, Failed, Spinner } from '../components/primitives.jsx';
import { bugReports } from '../lib/tomo.js';
import { useAsync } from '../useAsync.js';

export function Reports(): ReactNode {
  const [result, retry] = useAsync((signal) => bugReports.list(signal), []);

  return (
    <Shell>
      <h1 className="text-lg font-semibold text-ink-50">Reports</h1>
      <p className="mt-1 text-sm text-ink-400">
        What people told us went wrong, and what their browser said at the time.
      </p>

      {/*
        Three states kept apart, as everywhere else in this console: a failed
        request that renders as an empty list would say nobody has reported
        anything, which is the one wrong answer this screen can give.
      */}
      {result.state === 'loading' ? <Spinner label="Loading reports…" /> : null}
      {result.state === 'failed' ? (
        <Failed
          message="Could not load reports."
          detail={result.error.message}
          onRetry={retry}
        />
      ) : null}
      {result.state === 'ready' && result.value.length === 0 ? (
        <Empty title="Nobody has reported anything." />
      ) : null}
      {result.state === 'ready' && result.value.length > 0 ? (
        <div className="mt-4 space-y-3">
          {result.value.map((report) => <ReportCard key={report.id} report={report} />)}
        </div>
      ) : null}
    </Shell>
  );
}

function ReportCard({ report }: { readonly report: BugReportShape }): ReactNode {
  const [showShot, setShowShot] = useState(false);
  const d = report.diagnostics;

  return (
    <article className="rounded-lg border border-ink-800 bg-ink-900 p-4">
      <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
        <span className="font-semibold text-ink-50">
          {report.handle ?? 'Not signed in'}
        </span>
        <time className="text-ink-400" dateTime={report.created_at}>
          {new Date(report.created_at).toLocaleString()}
        </time>
        <code className="ml-auto text-xs text-ink-400">{d.path}</code>
      </header>

      <p className="mt-2 whitespace-pre-wrap text-sm text-ink-50">{report.description}</p>

      {/*
        THE DIAGNOSIS FIRST, above the raw envelope.

        A blocked embed script is the one cause that looks like a tomo bug
        from every angle — chat works, the room works, one pane is dead — so
        the screen says it rather than leaving it to be spotted in a field
        called `twitch_embed_loaded`.
      */}
      {!d.twitch_embed_loaded && (
        <p className="mt-2 rounded border border-warn-400/40 bg-warn-400/10 px-2 py-1.5 text-xs text-ink-50">
          <strong>Twitch&rsquo;s embed script did not load.</strong> Almost always a script
          blocker or an extension. Allowing this site is not enough: the player comes from
          <code className="mx-1">embed.twitch.tv</code>, which is a separate origin.
        </p>
      )}

      <dl className="mt-3 grid grid-cols-[9rem_1fr] gap-x-3 gap-y-1 text-xs">
        <Row label="Browser">{d.user_agent}</Row>
        <Row label="Window">{d.viewport}</Row>
        <Row label="Build">{d.app_version}</Row>
        <Row label="Embed script">{d.twitch_embed_loaded ? 'loaded' : 'NOT loaded'}</Row>
        <Row label="Cookies">{d.cookies_enabled ? 'enabled' : 'blocked'}</Row>
        <Row label="Storage access">{d.storage_access ? 'granted' : 'no'}</Row>
        {d.room_id !== null && <Row label="Room">{d.room_id}</Row>}
        {d.panes.length > 0 && <Row label="Panes">{d.panes.join(', ')}</Row>}
      </dl>

      {d.errors.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-ink-400">
            {d.errors.length} error{d.errors.length === 1 ? '' : 's'} recorded in that tab
          </summary>
          <pre className="mt-1 overflow-x-auto rounded bg-ink-950 p-2 text-[11px] leading-4 text-ink-200">
            {d.errors.join('\n')}
          </pre>
        </details>
      )}

      {report.screenshot_url !== null && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => { setShowShot((v) => !v); }}
            className="text-xs text-ink-400 underline hover:text-ink-50"
          >
            {showShot ? 'Hide screenshot' : 'Show screenshot'}
          </button>
          {showShot && (
            <img
              src={report.screenshot_url}
              alt={`Screenshot attached by ${report.handle ?? 'the reporter'}`}
              className="mt-2 w-full rounded border border-ink-800"
            />
          )}
        </div>
      )}
    </article>
  );
}

function Row({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <>
      <dt className="text-ink-400">{label}</dt>
      <dd className="break-words text-ink-200">{children}</dd>
    </>
  );
}
